import {
  AlertTriangle,
  CalendarDays,
  Clock,
  DoorOpen,
  GraduationCap,
  Plus,
  RefreshCw,
  Trash2,
  User,
  Users,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import AcademiaDateField from '../../common/AcademiaDateField';
import AcademiaSelect from '../../common/AcademiaSelect';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import AdminToast from '../AdminToast';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import AdminPageShell from '../AdminPageShell';
import { useAcademiaPlanning } from '../../../hooks/useAcademiaAdmin';
import { isAdministrateur } from '../../../utils/adminAccess';

const JOURS = ['LUNDI', 'MARDI', 'MERCREDI', 'JEUDI', 'VENDREDI', 'SAMEDI'] as const;

const JOUR_LABELS: Record<string, string> = {
  LUNDI: 'Lun',
  MARDI: 'Mar',
  MERCREDI: 'Mer',
  JEUDI: 'Jeu',
  VENDREDI: 'Ven',
  SAMEDI: 'Sam',
};

const JOUR_LABELS_LONG: Record<string, string> = {
  LUNDI: 'Lundi',
  MARDI: 'Mardi',
  MERCREDI: 'Mercredi',
  JEUDI: 'Jeudi',
  VENDREDI: 'Vendredi',
  SAMEDI: 'Samedi',
};

function todayIsoLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function jourSemaineFromIso(iso: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso.slice(0, 10))) return null;
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const map = [null, 'LUNDI', 'MARDI', 'MERCREDI', 'JEUDI', 'VENDREDI', 'SAMEDI'] as const;
  return map[d.getDay()] ?? null;
}

function dateRequiresJustification(iso: string, jourSemaine: string): boolean {
  const jourEffectif = jourSemaineFromIso(iso);
  return jourEffectif !== jourSemaine;
}

function calendarDayLabelFromIso(iso: string): string {
  const jour = jourSemaineFromIso(iso);
  if (jour) return JOUR_LABELS_LONG[jour] ?? jour;
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (d.getDay() === 0) return 'Dimanche';
  return 'autre jour';
}

type GridCell = {
  idVacation: string;
  idSalle?: string;
  vacationLibelle: string;
  jourSemaine: string;
  salleCode: string;
  salleLibelle: string;
  capacite: number;
  session: {
    id: string;
    filiere?: { id?: string; libelle?: string; code?: string };
    cohorte?: { id?: string; code?: string; libelle?: string } | null;
    formateur?: { id?: string; profil?: { prenom?: string; nom?: string } } | null;
    dateSeance?: string | null;
    justificationDateSeance?: string | null;
    presencesCount?: number;
  } | null;
};

type CohorteRow = {
  id: string;
  code?: string;
  libelle?: string;
  statut?: string;
  idFiliere?: string;
};

type FormateurRow = {
  id: string;
  nomComplet?: string;
  profil?: { prenom?: string; nom?: string };
};

type FormateurConflictRow = {
  idFormateur: string;
  formateurNom: string;
  jourSemaine: string;
  jourLabel: string;
  sessions: {
    id: string;
    vacationLibelle: string;
    heureDebut: string;
    heureFin: string;
    salleCode: string;
    salleLibelle: string;
    filiereCode: string;
    filiereLibelle: string;
  }[];
};

type FormateurConflictsReport = {
  totalConflicts: number;
  formateursEnConflit: number;
  conflicts: FormateurConflictRow[];
};

type ToastState = {
  message: string;
  variant: 'success' | 'error';
};

function formateurLabel(f: {
  id?: string;
  nomComplet?: string;
  profil?: { prenom?: string; nom?: string } | null;
}): string {
  const fromProfil = [f.profil?.prenom, f.profil?.nom].filter(Boolean).join(' ');
  const name = (f.nomComplet || fromProfil).trim();
  return name || 'Formateur';
}

type PlanningSessionCardProps = {
  session: NonNullable<GridCell['session']>;
  jourSemaine: string;
  cohortes: CohorteRow[];
  formateurs: FormateurRow[];
  onCohorte: (id: string | null) => void;
  onFormateur: (id: string | null) => void;
  onDate: (iso: string | null, justification: string | null) => Promise<void>;
  onDelete: () => Promise<void>;
  canDelete?: boolean;
};

function PlanningSessionCard({
  session,
  jourSemaine,
  cohortes,
  formateurs,
  onCohorte,
  onFormateur,
  onDate,
  onDelete,
  canDelete = false,
}: PlanningSessionCardProps) {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pendingDate, setPendingDate] = useState<string | null>(null);
  const [justificationDraft, setJustificationDraft] = useState('');
  const [dateError, setDateError] = useState<string | null>(null);
  const filiereLabel = session.filiere?.libelle || session.filiere?.code || 'Formation';
  const dateValue = session.dateSeance ? String(session.dateSeance).slice(0, 10) : '';
  const storedMismatch =
    dateValue && dateRequiresJustification(dateValue, jourSemaine) && session.justificationDateSeance;

  const cohorteOptions = cohortes.filter((coh) => {
    if (coh.statut !== 'OUVERTE' && coh.statut !== 'EN_COURS') return false;
    if (session.filiere?.id && coh.idFiliere !== session.filiere.id) return false;
    const cap = (coh as { capacity?: { placesRestantes?: number; estComplete?: boolean } }).capacity;
    if (cap?.estComplete || (cap?.placesRestantes ?? 1) <= 0) {
      return session.cohorte?.id === coh.id;
    }
    return true;
  });

  const assignedFormateurId = session.formateur?.id ?? '';
  const formateurIdsInList = new Set(formateurs.map((f) => f.id));
  const showAssignedFormateurOption =
    assignedFormateurId && !formateurIdsInList.has(assignedFormateurId);

  return (
    <div className="planning-cell-card planning-cell-card--active">
      <div className="planning-filiere-badge" title={filiereLabel}>
        <GraduationCap className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate">{filiereLabel}</span>
      </div>

      <div className="space-y-1">
        <span className="planning-field-label">
          <Users className="h-3 w-3" aria-hidden />
          Cohorte
        </span>
        <AcademiaSelect
          fieldSize="sm"
          className="w-full"
          value={session.cohorte?.id ?? ''}
          onChange={(e) => onCohorte(e.target.value || null)}
        >
          <option value="">Non assignée</option>
          {cohorteOptions.map((coh) => (
            <option key={coh.id} value={coh.id}>
              {coh.code || coh.libelle}
            </option>
          ))}
        </AcademiaSelect>
      </div>

      <div className="space-y-1">
        <span className="planning-field-label">
          <User className="h-3 w-3" aria-hidden />
          Formateur
        </span>
        <AcademiaSelect
          fieldSize="sm"
          className="w-full"
          value={assignedFormateurId}
          onChange={(e) => onFormateur(e.target.value || null)}
        >
          <option value="">Non assigné</option>
          {showAssignedFormateurOption ? (
            <option value={assignedFormateurId}>{formateurLabel(session.formateur!)}</option>
          ) : null}
          {formateurs.map((f) => (
            <option key={f.id} value={f.id}>
              {formateurLabel(f)}
            </option>
          ))}
        </AcademiaSelect>
      </div>

      <div className="space-y-1">
        <span className="planning-field-label">
          <CalendarDays className="h-3 w-3" aria-hidden />
          Date de séance
        </span>
        <AcademiaDateField
          hideLabel
          className="w-full"
          minDate={todayIsoLocal()}
          value={pendingDate ?? dateValue}
          onChange={(v) => {
            setDateError(null);
            if (!v) {
              setPendingDate(null);
              setJustificationDraft('');
              void onDate(null, null);
              return;
            }
            if (dateRequiresJustification(v, jourSemaine)) {
              setPendingDate(v);
              setJustificationDraft(session.justificationDateSeance ?? '');
              return;
            }
            setPendingDate(null);
            setJustificationDraft('');
            void onDate(v, null);
          }}
        />
        <p className="text-[10px] leading-snug text-on-surface-variant">
          Créneau grille : {JOUR_LABELS_LONG[jourSemaine] ?? jourSemaine}. Dates futures autorisées.
        </p>
        {storedMismatch && !pendingDate ? (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[10px] text-amber-900">
            <span className="font-bold">Report justifié :</span> {session.justificationDateSeance}
          </p>
        ) : null}
        {pendingDate ? (
          <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50/80 p-2">
            <p className="text-[11px] font-semibold text-amber-950">
              Cette date tombe un {calendarDayLabelFromIso(pendingDate)} alors
              que le créneau est prévu le {JOUR_LABELS_LONG[jourSemaine] ?? jourSemaine}. Une note justificative est
              obligatoire.
            </p>
            <textarea
              rows={3}
              className="w-full rounded-md border border-amber-200 bg-white px-2 py-1.5 text-xs text-slate-900 focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15"
              value={justificationDraft}
              onChange={(e) => setJustificationDraft(e.target.value)}
              placeholder="Ex. : report exceptionnel pour jour férié ou indisponibilité de la salle…"
            />
            {dateError ? <p className="text-[10px] font-semibold text-red-700">{dateError}</p> : null}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                className="rounded-md bg-primary px-2.5 py-1 text-[11px] font-bold text-white disabled:opacity-50"
                onClick={() => {
                  if (justificationDraft.trim().length < 10) {
                    setDateError('La note justificative doit contenir au moins 10 caractères.');
                    return;
                  }
                  setBusy(true);
                  void onDate(pendingDate, justificationDraft.trim())
                    .then(() => {
                      setPendingDate(null);
                      setJustificationDraft('');
                      setDateError(null);
                    })
                    .catch((e: unknown) => {
                      setDateError(e instanceof Error ? e.message : 'Enregistrement impossible.');
                    })
                    .finally(() => setBusy(false));
                }}
              >
                Valider la date
              </button>
              <button
                type="button"
                disabled={busy}
                className="rounded-md border border-amber-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-amber-900"
                onClick={() => {
                  setPendingDate(null);
                  setJustificationDraft('');
                  setDateError(null);
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {canDelete ? (
      <button
        type="button"
        disabled={busy}
        className="flex w-full items-center justify-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-xs font-bold text-red-700 disabled:opacity-50"
        onClick={() => {
          setDeleteError(null);
          setConfirmOpen(true);
        }}
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
        {busy ? 'Suppression…' : 'Supprimer session'}
      </button>
      ) : null}

      <DeleteConfirmModal
        open={confirmOpen}
        title="Supprimer cette session ?"
        description={
          (session.presencesCount ?? 0) > 0
            ? `Cette action est irréversible. ${session.presencesCount} présence(s) enregistrée(s) seront également supprimée(s).`
            : 'Cette action est irréversible. Le créneau redeviendra libre.'
        }
        isConfirming={busy}
        errorMessage={deleteError}
        onCancel={() => {
          if (!busy) setConfirmOpen(false);
        }}
        onConfirm={async () => {
          if (busy) return;
          setBusy(true);
          setDeleteError(null);
          try {
            await onDelete();
            setConfirmOpen(false);
          } catch (e) {
            setDeleteError(e instanceof Error ? e.message : 'Suppression impossible.');
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
}

type FiliereRow = { id: string; libelle?: string; code?: string };

function FreeSlotCard({
  cell,
  jourSemaine,
  filieres,
  onCreate,
}: {
  cell: GridCell;
  jourSemaine: string;
  filieres: FiliereRow[];
  onCreate: (payload: {
    idFiliere: string;
    idSalle: string;
    idVacation: string;
    jourSemaine: string;
  }) => Promise<void>;
}) {
  const [idFiliere, setIdFiliere] = useState(filieres[0]?.id ?? '');
  const [busy, setBusy] = useState(false);

  if (!cell.idSalle) {
    return (
      <div className="planning-cell-card planning-cell-card--free">
        <span className="text-xs text-on-surface-variant">Créneau libre</span>
      </div>
    );
  }

  return (
    <div className="planning-cell-card planning-cell-card--free space-y-2">
      <DoorOpen className="mx-auto h-6 w-6 text-on-surface-variant/40" aria-hidden />
      <span className="block text-center text-xs font-semibold text-on-surface-variant">Créneau libre</span>
      {filieres.length > 0 ? (
        <AcademiaSelect
          fieldSize="sm"
          className="w-full"
          value={idFiliere}
          onChange={(e) => setIdFiliere(e.target.value)}
        >
          {filieres.map((f) => (
            <option key={f.id} value={f.id}>
              {f.libelle || f.code || f.id}
            </option>
          ))}
        </AcademiaSelect>
      ) : null}
      <button
        type="button"
        disabled={!idFiliere || busy}
        className="flex w-full items-center justify-center gap-1 rounded-lg border border-primary/30 bg-primary/5 px-2 py-1.5 text-xs font-bold text-primary disabled:opacity-50"
        onClick={async () => {
          if (!idFiliere || !cell.idSalle) return;
          setBusy(true);
          try {
            await onCreate({
              idFiliere,
              idSalle: cell.idSalle,
              idVacation: cell.idVacation,
              jourSemaine,
            });
          } finally {
            setBusy(false);
          }
        }}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden />
        {busy ? 'Création…' : 'Créer session'}
      </button>
    </div>
  );
}

export default function AcademiaPlanningPanel({ user }: { user?: { role?: string } | null }) {
  const canDelete = isAdministrateur(user);
  const planning = useAcademiaPlanning();
  useRegisterPortalRefresh(() => void planning.refetch());
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const runPlanningAction = useCallback(
    async (
      action: () => Promise<unknown>,
      successMessage: string,
      errorFallback = 'Action impossible.',
    ) => {
      try {
        await action();
        showToast(successMessage, 'success');
      } catch (error) {
        const message = error instanceof Error ? error.message : errorFallback;
        showToast(message, 'error');
        throw error;
      }
    },
    [showToast],
  );

  const cellsForDay = useMemo(() => {
    const cells = (planning.grid?.cells ?? []) as GridCell[];
    return cells.filter((c) => c.jourSemaine === planning.selectedJour);
  }, [planning.grid, planning.selectedJour]);

  const vacations = useMemo(() => {
    const vacIds = new Set(cellsForDay.map((c) => c.idVacation));
    const fromGrid = (planning.grid?.vacations ?? []) as { id: string; libelle: string; ordre: number }[];
    return fromGrid.filter((v) => vacIds.has(v.id)).sort((a, b) => a.ordre - b.ordre);
  }, [cellsForDay, planning.grid]);

  const salles = useMemo(() => {
    const codes = ['SALLE_1', 'SALLE_2', 'SALLE_3'];
    return codes.map((code) => {
      const cell = cellsForDay.find((c) => c.salleCode === code);
      return {
        code,
        libelle: cell?.salleLibelle ?? code.replace('_', ' '),
        capacite: cell?.capacite ?? null,
      };
    });
  }, [cellsForDay]);

  const allCohortes = planning.cohortes as CohorteRow[];
  const allFormateurs = planning.formateurs as FormateurRow[];
  const allFilieres = planning.filieres as FiliereRow[];

  const gridTemplateColumns = useMemo(
    () => `minmax(9.5rem, 11rem) repeat(${salles.length}, minmax(15rem, 1fr))`,
    [salles.length],
  );

  const sessionsDuJour = useMemo(
    () => cellsForDay.filter((c) => c.session).length,
    [cellsForDay],
  );

  const formateurConflicts = (planning.formateurConflicts ?? {
    totalConflicts: 0,
    formateursEnConflit: 0,
    conflicts: [],
  }) as FormateurConflictsReport;

  const sideCard = {
    label: 'Sessions planifiées',
    value: planning.isLoading ? '…' : String(planning.grid?.totalSessions ?? 0),
    helper:
      formateurConflicts.totalConflicts > 0
        ? `${formateurConflicts.totalConflicts} conflit(s) formateur à corriger`
        : 'Grille Lun–Mer–Ven / Mar–Jeu–Sam',
    sideLinks: [
      { label: 'Charge horaire', to: '/admin/charge-horaire', icon: 'event' },
      { label: 'Formations', to: '/admin/formations', icon: 'school' },
      { label: 'Logistique', to: '/admin/logistique', icon: 'inventory_2' },
    ],
  };

  return (
    <AdminPageShell
      title="Planning des vacations"
      sideCard={sideCard}
      description="Occupation des salles par créneau horaire. Liez une cohorte ouverte à une session."
      onRetry={() => void planning.refetch()}
      isLoading={planning.isLoading}
      isError={planning.isError}
      loadingMessage="Chargement du planning…"
      errorMessage="Impossible de charger le planning."
    >
      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={dismissToast}
      />

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
        <div className="planning-day-rail" role="tablist" aria-label="Jour de la semaine">
          {JOURS.map((jour) => (
            <button
              key={jour}
              type="button"
              role="tab"
              aria-selected={planning.selectedJour === jour}
              onClick={() => planning.setSelectedJour(jour)}
              className={`planning-day-btn ${planning.selectedJour === jour ? 'planning-day-btn--active' : ''}`}
              title={JOUR_LABELS_LONG[jour]}
            >
              <span className="hidden sm:inline">{JOUR_LABELS_LONG[jour]}</span>
              <span className="sm:hidden">{JOUR_LABELS[jour] ?? jour}</span>
            </button>
          ))}
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-lg border border-outline-variant/30 bg-white px-3 py-1.5 text-xs font-bold text-on-surface-variant hover:border-primary/40 hover:text-primary"
          onClick={() =>
            void runPlanningAction(
              () => planning.regenerateGrid(),
              'Grille régénérée avec succès.',
              'Impossible de régénérer la grille.',
            )
          }
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          Régénérer la grille
        </button>
        </div>
        <p className="text-sm text-on-surface-variant">
          <span className="font-bold text-primary">{sessionsDuJour}</span> session(s) ce{' '}
          {JOUR_LABELS_LONG[planning.selectedJour]?.toLowerCase() ?? planning.selectedJour}
        </p>
      </div>

      {planning.grid?.totalSessions === 0 && !planning.isLoading ? (
        <p className="mb-4 rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          Aucune session n&apos;est encore planifiée. Cliquez sur{' '}
          <span className="font-semibold">Régénérer la grille</span> pour créer les créneaux par salle et
          vacation, puis liez une cohorte ouverte à chaque session.
        </p>
      ) : null}

      <div className="planning-board">
        <div className="planning-board-scroll">
          <div className="planning-grid" style={{ gridTemplateColumns }}>
            <div className="planning-corner">
              <Clock className="h-4 w-4 text-primary" aria-hidden />
              Créneaux
            </div>
            {salles.map((s) => (
              <div key={s.code} className="planning-salle-head">
                <div className="flex items-start gap-2">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <DoorOpen className="h-5 w-5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="planning-salle-head__title">{s.libelle}</p>
                    {s.capacite != null ? (
                      <span className="planning-salle-head__meta">
                        <Users className="h-3 w-3" aria-hidden />
                        {s.capacite} places max
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}

            {vacations.map((vac) => {
              const vacId = vac.id;
              const vacLabel =
                'libelle' in vac && vac.libelle
                  ? vac.libelle
                  : cellsForDay.find((c) => c.idVacation === vacId)?.vacationLibelle ?? '—';

              return (
                <div key={vacId} className="contents">
                  <div className="planning-vacation-label">
                    <span className="planning-vacation-label__badge">Vacation</span>
                    <span className="planning-vacation-label__time">{vacLabel}</span>
                  </div>
                  {salles.map((s) => {
                    const cell = cellsForDay.find(
                      (c) => c.idVacation === vacId && c.salleCode === s.code,
                    );
                    const session = cell?.session;

                    return (
                      <div
                        key={`${vacId}-${s.code}`}
                        className={`planning-cell ${session ? 'planning-cell--occupied' : ''}`}
                      >
                        {session ? (
                          <PlanningSessionCard
                            session={session}
                            jourSemaine={cell.jourSemaine}
                            cohortes={allCohortes}
                            formateurs={allFormateurs}
                            onCohorte={(id) =>
                              void runPlanningAction(
                                () => planning.assignCohorte(session.id, id),
                                id
                                  ? 'Cohorte assignée à la session.'
                                  : 'Cohorte retirée de la session.',
                                "Impossible de modifier la cohorte.",
                              )
                            }
                            onFormateur={(id) =>
                              void runPlanningAction(
                                () => planning.assignFormateur(session.id, id),
                                id
                                  ? 'Formateur assigné à la session.'
                                  : 'Formateur retiré de la session.',
                                "Impossible de modifier le formateur.",
                              )
                            }
                            onDate={(iso, justification) =>
                              runPlanningAction(
                                () => planning.assignDateSeance(session.id, iso, justification),
                                iso
                                  ? justification
                                    ? 'Date de séance et justification enregistrées.'
                                    : 'Date de séance enregistrée.'
                                  : 'Date de séance retirée.',
                                "Impossible de modifier la date de séance.",
                              )
                            }
                            onDelete={async () => {
                              await runPlanningAction(
                                () => planning.deleteSessionById(session.id),
                                'Session supprimée avec succès.',
                                'Impossible de supprimer la session.',
                              );
                            }}
                            canDelete={canDelete}
                          />
                        ) : cell ? (
                          <FreeSlotCard
                            cell={cell}
                            jourSemaine={planning.selectedJour}
                            filieres={allFilieres}
                            onCreate={async (payload) => {
                              await runPlanningAction(
                                () => planning.createSessionOnSlot(payload),
                                'Session créée avec succès.',
                                'Impossible de créer la session.',
                              );
                            }}
                          />
                        ) : (
                          <div className="planning-cell-card planning-cell-card--free">
                            <span className="text-xs text-on-surface-variant">—</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <section className="mt-8" aria-labelledby="planning-formateur-conflicts-title">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <AlertTriangle
              className={`h-5 w-5 ${formateurConflicts.totalConflicts > 0 ? 'text-amber-600' : 'text-primary'}`}
              aria-hidden
            />
            <h3 id="planning-formateur-conflicts-title" className="text-base font-black text-slate-900">
              Conflits formateurs
            </h3>
          </div>
          {formateurConflicts.totalConflicts > 0 ? (
            <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-900">
              {formateurConflicts.totalConflicts} conflit(s) · {formateurConflicts.formateursEnConflit}{' '}
              formateur(s)
            </span>
          ) : null}
        </div>

        {formateurConflicts.totalConflicts === 0 ? (
          <p className="rounded-xl border border-emerald-200/80 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            Aucun formateur n&apos;est affecté à deux séances simultanées (même jour, créneaux qui se
            chevauchent).
          </p>
        ) : (
          <ul className="space-y-3">
            {formateurConflicts.conflicts.map((row, index) => (
              <li
                key={`${row.idFormateur}-${row.jourSemaine}-${index}`}
                className="rounded-xl border border-amber-200/90 bg-amber-50/80 px-4 py-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-slate-900">{row.formateurNom}</p>
                    <p className="mt-1 text-sm text-amber-950">
                      {row.jourLabel.charAt(0).toUpperCase() + row.jourLabel.slice(1)} —{' '}
                      {row.sessions.length} séance(s) en chevauchement
                    </p>
                  </div>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-lg border border-amber-300/80 bg-white px-3 py-1.5 text-xs font-bold text-amber-900 hover:bg-amber-100/50"
                    onClick={() => planning.setSelectedJour(row.jourSemaine)}
                  >
                    <CalendarDays className="h-3.5 w-3.5" aria-hidden />
                    Voir le {JOUR_LABELS[row.jourSemaine] ?? row.jourSemaine}
                  </button>
                </div>
                <ul className="mt-3 space-y-2">
                  {row.sessions.map((session) => (
                    <li
                      key={session.id}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-amber-200/60 bg-white/90 px-3 py-2 text-xs text-slate-800"
                    >
                      <span className="font-bold text-primary">{session.vacationLibelle}</span>
                      <span className="text-on-surface-variant">
                        {session.heureDebut}–{session.heureFin}
                      </span>
                      <span>
                        <DoorOpen className="mr-0.5 inline h-3 w-3" aria-hidden />
                        {session.salleCode}
                      </span>
                      <span>
                        <GraduationCap className="mr-0.5 inline h-3 w-3" aria-hidden />
                        {session.filiereCode} — {session.filiereLibelle}
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-8">
        <div className="mb-4 flex items-center gap-2">
          <DoorOpen className="h-5 w-5 text-primary" aria-hidden />
          <h3 className="text-base font-black text-slate-900">État des salles</h3>
        </div>
        <ul className="grid gap-3 sm:grid-cols-3">
          {(
            planning.availability as {
              salle?: { libelle?: string };
              sessionsCount?: number;
              conflits?: number;
            }[]
          ).map((row) => {
            const hasConflict = (row.conflits ?? 0) > 0;
            return (
              <li
                key={row.salle?.libelle ?? 'salle'}
                className={`planning-availability-card ${hasConflict ? 'planning-availability-card--warn' : ''}`}
              >
                <p className="font-bold text-slate-900">{row.salle?.libelle}</p>
                <p className="mt-1 text-sm text-on-surface-variant">
                  {row.sessionsCount ?? 0} session(s) planifiée(s)
                </p>
                {hasConflict ? (
                  <p className="mt-2 text-xs font-bold text-amber-800">
                    {row.conflits} conflit(s) détecté(s)
                  </p>
                ) : (
                  <p className="mt-2 text-xs font-medium text-emerald-700">Aucun conflit</p>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </AdminPageShell>
  );
}
